import { Title, Text, SimpleGrid, Card, rem, Container, ThemeIcon, Box, Stack } from '@mantine/core';
import { motion } from 'framer-motion';
import { HOME_CONTENT } from '../../data/homeContent';

const MotionCard = motion(Card);

const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.2 } }
};

const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
};

/**
 * HowItWorks component provides a visual walkthrough of the process to start using the bot tool.
 * It uses a background image with overlays to create a focused, high-contrast section.
 */
export default function HowItWorks() {
  const { howItWorks } = HOME_CONTENT;
  return (
    <Box
        id="how-it-works"
        style={{
            position: 'relative',
            overflow: 'hidden',
            backgroundColor: '#050505'
        }}
    >
        <div style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: 'url("/kahoot_game.png")',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            opacity: 0.3,
            zIndex: 0,
            filter: 'grayscale(70%) blur(2px)'
        }} />

        <div style={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to bottom, #050505 0%, rgba(5,5,5,0.8) 20%, rgba(5,5,5,0.8) 80%, #050505 100%)',
            zIndex: 1
        }} />

        <Container size="lg" py={{ base: 80, md: 120 }} style={{ position: 'relative', zIndex: 2 }}>
            <Stack align="center" mb={60}>
                <Title
                    order={2}
                    ta="center"
                    fw={900}
                    style={{ fontSize: '2.5rem', letterSpacing: '-1px', lineHeight: 1.1 }}
                >
                    {howItWorks.title} <span style={{ color: '#228be6' }}>{howItWorks.titleHighlight}</span>
                </Title>
                <Text c="dimmed" ta="center" size="lg" maw={500}>
                    {howItWorks.subtitle}
                </Text>
            </Stack>

            <SimpleGrid
                cols={{ base: 1, md: 3 }}
                spacing={30}
                component={motion.div}
                variants={containerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-20px" }}
            >
                {howItWorks.steps.map((step) => (
                    <MotionCard
                        key={step.step}
                        padding="xl"
                        radius="lg"
                        withBorder
                        variants={cardVariants}
                        style={{
                            backgroundColor: 'rgba(10, 10, 10, 0.7)', // Slightly darker for contrast
                            borderColor: 'rgba(255, 255, 255, 0.1)',
                            backdropFilter: 'blur(10px)',
                            position: 'relative',
                            overflow: 'hidden'
                        }}
                        whileHover={{
                            y: -5,
                            borderColor: `var(--mantine-color-${step.color}-8)`,
                            boxShadow: `0 10px 40px rgba(0,0,0,0.5)`
                        }}
                    >
                        {/* Giant Watermark Number */}
                        <Text
                            style={{
                                position: 'absolute',
                                top: -20,
                                right: -10,
                                fontSize: '8rem',
                                fontWeight: 900,
                                color: 'white',
                                opacity: 0.03,
                                lineHeight: 1,
                                pointerEvents: 'none',
                                zIndex: 0
                            }}
                        >
                            {step.step}
                        </Text>

                        <Box style={{ position: 'relative', zIndex: 1 }}>
                            <ThemeIcon
                                size={60}
                                radius="md"
                                variant="light"
                                color={step.color}
                                mb="lg"
                                style={{ backgroundColor: `rgba(var(--mantine-color-${step.color}-9), 0.15)` }}
                            >
                                <step.icon style={{ width: rem(30), height: rem(30) }} stroke={1.5} />
                            </ThemeIcon>

                            <Text fz="xl" fw={700} c="white" mb="sm">
                                {step.title}
                            </Text>

                            <Text fz="sm" c="dimmed" lh={1.6}>
                                {step.description}
                            </Text>
                        </Box>
                    </MotionCard>
                ))}
            </SimpleGrid>
        </Container>
    </Box>
  );
}